import { App, Form, Input, Select } from 'antd';
import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { http } from '../../api/client.js';
import { FancyModal } from '../../components/ui/index.js';
import type { AdminRoleRow, PlatformRoleType } from './platform-permission-types.js';

interface CreatePlatformRoleFormValues {
  name: string;
  title?: string;
  description?: string;
  roleType: PlatformRoleType;
}

const ROLE_TYPE_OPTIONS = [
  { value: 'user', label: '用户' },
  { value: 'admin', label: '管理' },
  { value: 'system', label: '系统' },
];

export function CreatePlatformRoleModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (role: AdminRoleRow) => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm<CreatePlatformRoleFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const response = await http.post<AdminRoleRow>('/admin/platform-roles', {
        name: values.name.trim(),
        title: values.title?.trim() || null,
        description: values.description?.trim() || null,
        roleType: values.roleType,
        viewAllData: false,
      });
      message.success(`平台角色「${values.name.trim()}」已创建`);
      form.resetFields();
      onCreated(response.data);
      onClose();
    } catch (error) {
      const detail =
        typeof error === 'object' &&
        error &&
        'response' in error &&
        typeof (error as { response?: { data?: { message?: unknown } } }).response?.data
          ?.message === 'string'
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      message.error(detail || '创建平台角色失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FancyModal
      open={open}
      onCancel={onClose}
      onOk={submit}
      title="新建平台角色"
      subtitle="平台角色只用于 CREST 应用访问、系统菜单与平台数据权限。"
      icon={<ShieldCheck size={18} />}
      okText="创建"
      cancelText="取消"
      confirmLoading={submitting}
      destroyOnClose
      width={560}
    >
      <Form form={form} layout="vertical" preserve={false} initialValues={{ roleType: 'user' }}>
        <Form.Item
          label="角色名"
          name="name"
          rules={[
            { required: true, whitespace: true, message: '请输入角色名' },
            { max: 100, message: '角色名不能超过 100 个字符' },
          ]}
          tooltip="角色名必须全局唯一，建议使用简短中文或英文标识。"
        >
          <Input placeholder="例如：普通员工" allowClear />
        </Form.Item>

        <Form.Item label="显示名" name="title">
          <Input placeholder="可选，用于补充展示名称" allowClear />
        </Form.Item>

        <Form.Item label="描述" name="description">
          <Input.TextArea rows={2} placeholder="说明这个平台角色的用途" allowClear />
        </Form.Item>

        <Form.Item
          label="角色类型"
          name="roleType"
          rules={[{ required: true, message: '请选择角色类型' }]}
        >
          <Select options={ROLE_TYPE_OPTIONS} />
        </Form.Item>
      </Form>
    </FancyModal>
  );
}
